import { type, type Traversal, type Type } from 'arktype';
import { dateKey, listMetaSchema, type ListMeta } from '@zag/core/models';
import { DEFAULT_TERMINAL_TIME_ZONE, TERMINAL_LOGICAL_ID_MAX_LENGTH, TERMINAL_NAME_MAX_LENGTH } from './terminal-defaults';

const uuidString = type('string.uuid').configure({ description: 'UUID.' });
const nullableTimestamp = type('string | null').configure({ description: 'ISO 8601 timestamp, or null.' });
const timeZoneString = type('string > 0').configure({
  description: 'IANA time zone the terminal clock runs in.',
  examples: [DEFAULT_TERMINAL_TIME_ZONE],
});
const positiveFeedbackFlag = type('boolean').configure({
  description: 'Whether the terminal signals accepted scans with positive feedback.',
});
const terminalNameInput = type('string.trim')
  .to(`1 <= string <= ${TERMINAL_NAME_MAX_LENGTH}`)
  .configure({ description: 'Human-readable terminal name, trimmed, 1–80 characters; not unique.' });
const showBalancesFlag = type('boolean').configure({
  description: 'Whether accepted scans and the balance key reply with the balances of the person.',
});

export type TerminalConnectionState = 'online' | 'warning' | 'offline' | 'never';

export const terminalConnectionStateSchema: Type<TerminalConnectionState> = type("'online' | 'warning' | 'offline' | 'never'").configure({
  description: 'Server-side connection health derived from the last contact (D10 thresholds); never = no contact yet.',
});

export type TerminalLifecycleState = 'registered' | 'active' | 'disabled' | 'decommissioned';

export const terminalLifecycleStateSchema: Type<TerminalLifecycleState> = type(
  "'registered' | 'active' | 'disabled' | 'decommissioned'"
).configure({
  description: 'Lifecycle state of a terminal.',
});

export interface TerminalListItem {
  id: string;
  name: string;
  logicalId: string;
  providerKey: string;
  profileKey: string | null;
  state: TerminalLifecycleState;
  timeZone: string;
  positiveFeedbackEnabled: boolean;
  showBalances: boolean;
  lastSeenAt: string | null;
  connection: TerminalConnectionState;
  credential: { active: boolean; expiresAt: string | null };
}

export const terminalListItemSchema: Type<TerminalListItem> = type({
  id: uuidString,
  name: type('string').configure({ description: 'Human-readable terminal name.' }),
  logicalId: type('string').configure({ description: 'Logical device id the terminal reports on the wire.' }),
  providerKey: type('string').configure({ description: 'Terminal provider (protocol family) key.', examples: ['tmc-x'] }),
  profileKey: type('string | null').configure({ description: 'Pinned hardware profile key, or null when unidentified.' }),
  state: terminalLifecycleStateSchema,
  timeZone: timeZoneString,
  positiveFeedbackEnabled: positiveFeedbackFlag,
  showBalances: showBalancesFlag,
  lastSeenAt: nullableTimestamp.configure({ description: 'Last server contact (ISO 8601), or null if never connected.' }),
  connection: terminalConnectionStateSchema,
  credential: type({
    active: type('boolean').configure({ description: 'Whether an unrevoked, unexpired credential exists.' }),
    expiresAt: nullableTimestamp.configure({ description: 'Expiry of the active credential (ISO 8601), or null.' }),
  }).configure({ description: 'Credential status; the secret itself is never returned.' }),
}).configure({ description: 'Terminal row in the terminal collection.' });

export interface TerminalListResponse {
  data: TerminalListItem[];
  meta: ListMeta;
}

export const terminalListResponseSchema: Type<TerminalListResponse> = type({
  data: terminalListItemSchema.array(),
  meta: listMetaSchema,
}).configure({ description: 'Paginated terminal collection.' });

export interface CreateTerminal {
  name: string;
  logicalId: string;
  providerKey: 'tmc-x';
  profileKey: 'x3-j02';
  timeZone?: string;
  positiveFeedbackEnabled?: boolean;
  showBalances?: boolean;
}

export const createTerminalSchema = type({
  name: terminalNameInput,
  logicalId: type('string > 0')
    .atMostLength(TERMINAL_LOGICAL_ID_MAX_LENGTH)
    .configure({ description: 'Logical device id configured on the terminal.' }),
  providerKey: type("'tmc-x'").configure({ description: 'Terminal provider key.' }),
  profileKey: type("'x3-j02'").configure({ description: 'Qualified hardware profile key.' }),
  'timeZone?': timeZoneString,
  'positiveFeedbackEnabled?': positiveFeedbackFlag,
  'showBalances?': showBalancesFlag,
}).configure({ description: 'Payload to register a terminal.' });

export interface UpdateTerminal {
  name?: string;
  state?: 'active' | 'disabled' | 'decommissioned';
  timeZone?: string;
  positiveFeedbackEnabled?: boolean;
  showBalances?: boolean;
}

export const updateTerminalSchema = type({
  'name?': terminalNameInput,
  'state?': type("'active' | 'disabled' | 'decommissioned'").configure({ description: 'Target lifecycle state.' }),
  'timeZone?': timeZoneString,
  'positiveFeedbackEnabled?': positiveFeedbackFlag,
  'showBalances?': showBalancesFlag,
}).configure({ description: 'Partial terminal update.' });

export interface IssuedTerminalCredential {
  authToken: string;
  expiresAt: string;
}

export const issuedTerminalCredentialSchema: Type<IssuedTerminalCredential> = type({
  authToken: type('string').configure({
    description: 'Full Authorization header value (`Bearer term_…`) to configure on the terminal. Shown once.',
  }),
  expiresAt: type('string').configure({ description: 'Credential expiry (ISO 8601).' }),
}).configure({ description: 'Newly issued terminal credential; the token is never retrievable again.' });

export interface DeleteTerminalResult {
  result: 'deleted' | 'archived';
}

export const deleteTerminalResultSchema: Type<DeleteTerminalResult> = type({
  result: type("'deleted' | 'archived'").configure({
    description: 'deleted = removed with its credentials; archived = decommissioned because it already delivered data.',
  }),
}).configure({ description: 'Outcome of removing a terminal.' });

export interface PersonBadgeAssignment {
  id: string;
  personId: string;
  badge: string;
  validFromDate: string;
  validThroughDate: string | null;
  revokedAt: string | null;
  active: boolean;
}

export const personBadgeAssignmentSchema: Type<PersonBadgeAssignment> = type({
  id: uuidString,
  personId: uuidString,
  badge: type('string').configure({ description: 'Badge number as read by the terminal.' }),
  validFromDate: dateKey,
  validThroughDate: dateKey.or(type('null')).configure({ description: 'Inclusive last valid civil day, or null.' }),
  revokedAt: nullableTimestamp.configure({ description: 'Revocation time (ISO 8601), or null.' }),
  active: type('boolean').configure({ description: 'Whether the assignment is valid today and not revoked.' }),
}).configure({ description: 'Badge assigned to a person for a validity period.' });

export interface PersonBadgeListResponse {
  data: PersonBadgeAssignment[];
}

export const personBadgeListResponseSchema: Type<PersonBadgeListResponse> = type({
  data: personBadgeAssignmentSchema.array(),
}).configure({ description: 'Badge assignments of one person.' });

export interface CreatePersonBadge {
  badge: string;
  validFromDate: string;
  validThroughDate?: string | null;
}

export const createPersonBadgeSchema: Type<CreatePersonBadge> = type({
  badge: type('1 <= string <= 64').configure({ description: 'Badge number as read by the terminal.' }),
  validFromDate: dateKey,
  'validThroughDate?': dateKey.or(type('null')).configure({ description: 'Inclusive last valid civil day; null = open.' }),
}).configure({ description: 'Payload to assign a badge to a person.' });

export interface EndPersonBadge {
  validThroughDate: string;
}

export const endPersonBadgeSchema: Type<EndPersonBadge> = type({
  validThroughDate: dateKey.configure({ description: 'Inclusive last valid civil day.' }),
}).configure({ description: 'Payload to end a badge assignment.' });

const withoutDuplicates = (ids: string[], ctx: Traversal) => new Set(ids).size === ids.length || ctx.mustBe('a list without duplicates');
const uniqueUuids = (description: string) => uuidString.array().narrow(withoutDuplicates).configure({ description });
const terminalIdList = uniqueUuids('Terminal ids, without duplicates.');
const groupIdList = uniqueUuids('Terminal group ids, without duplicates.');

export interface TerminalGroup {
  id: string;
  name: string;
  terminalIds: string[];
}

export const terminalGroupSchema: Type<TerminalGroup> = type({
  id: uuidString,
  name: type('string').configure({ description: 'Group name, unique case-insensitively.' }),
  terminalIds: uuidString.array().configure({ description: 'Ids of the non-decommissioned member terminals.' }),
}).configure({ description: 'Named group of terminals; a terminal can belong to several groups.' });

export interface TerminalGroupListResponse {
  data: TerminalGroup[];
  meta: ListMeta;
}

export const terminalGroupListResponseSchema: Type<TerminalGroupListResponse> = type({
  data: terminalGroupSchema.array(),
  meta: listMetaSchema,
}).configure({ description: 'Terminal groups ordered by name.' });

export interface UpsertTerminalGroup {
  name: string;
  terminalIds: string[];
}

export const upsertTerminalGroupSchema = type({
  name: type('string.trim')
    .to('1 <= string <= 80')
    .configure({ description: 'Group name, trimmed, 1–80 characters, unique case-insensitively.' }),
  terminalIds: terminalIdList,
}).configure({ description: 'Payload to create or fully replace a terminal group.' });

export interface TerminalGroupPerson {
  personId: string;
  firstName: string;
  lastName: string;
}

export const terminalGroupPersonSchema: Type<TerminalGroupPerson> = type({
  personId: uuidString,
  firstName: 'string',
  lastName: 'string',
}).configure({ description: 'Person wired to a terminal group.' });

export interface TerminalGroupPeopleResponse {
  data: TerminalGroupPerson[];
  meta: ListMeta;
}

export const terminalGroupPeopleResponseSchema: Type<TerminalGroupPeopleResponse> = type({
  data: terminalGroupPersonSchema.array(),
  meta: listMetaSchema,
}).configure({ description: 'People wired to the group within your scope, ordered by last and first name.' });

export interface TerminalGroupPeopleChange {
  personIds: string[];
}

export const terminalGroupPeopleChangeSchema: Type<TerminalGroupPeopleChange> = type({
  personIds: uuidString
    .array()
    .atLeastLength(1)
    .atMostLength(500)
    .narrow(withoutDuplicates)
    .configure({ description: 'Person ids, 1–500, without duplicates.' }),
}).configure({ description: 'Persons to wire to or unwire from the group.' });

export interface PersonTerminalWiring {
  terminalIds: string[];
  groupIds: string[];
}

export const personTerminalWiringSchema: Type<PersonTerminalWiring> = type({
  terminalIds: terminalIdList,
  groupIds: groupIdList,
}).configure({ description: 'Terminals and groups a person may stamp at; both empty = every terminal.' });

export type ReplacePersonTerminalWiring = PersonTerminalWiring;

export const replacePersonTerminalWiringSchema: Type<ReplacePersonTerminalWiring> = personTerminalWiringSchema.configure({
  description: 'Payload replacing the whole wiring of a person; both lists empty allows every terminal.',
});
